import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/providers/auth-provider';

// Type to represent data returned from the API
export type ProfileData = {
  id: string;
  email: string;
  userName: string | null;
  avatarUrl: string | null;
  githubUserId: string | null;
  hasMrgeAccount: boolean;
  is_super_admin: boolean;
  hasUsedmgCLI: boolean;
  cliVersion: string | null;
  /** Last time the cubic desktop app reported in, as an ISO timestamp. */
  desktopAppLastSeenAt: string | null;
  selectedInstallationId: number | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * Query key for the profile data
 */
export const profileQueryKey = () => ['profile'] as const;

/**
 * Function to fetch the current user's profile
 */
export async function getProfileFn(): Promise<ProfileData> {
  const response = await fetch(`/api/profile/me`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch profile');
  }
  return response.json();
}

/**
 * Function to update the current user's profile
 */
export async function updateProfileFn(data: Partial<ProfileData>): Promise<ProfileData> {
  const response = await fetch(`/api/profile/me`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update profile');
  }
  return response.json();
}

/**
 * Hook to fetch the current user's profile
 */
export function useProfile() {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  return useQuery({
    queryKey: profileQueryKey(),
    queryFn: getProfileFn,
    enabled: isAuthenticated && !isAuthLoading,
  });
}

/**
 * Hook to update the user's profile, with specific support for
 * updating the selectedInstallationId
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProfileFn,
    onSuccess: () => {
      // Invalidate and refetch the profile data
      queryClient.invalidateQueries({ queryKey: profileQueryKey() });
    },
  });
}

/**
 * Special hook just for updating the selected installation ID
 */
export function useUpdateSelectedInstallation() {
  const { mutate, isPending, isError, error } = useUpdateProfile();

  const updateSelectedInstallation = (selectedInstallationId: number | null) => {
    mutate({ selectedInstallationId });
  };

  return {
    updateSelectedInstallation,
    isUpdating: isPending,
    isError,
    error,
  };
}
